"use client";

import { Suspense, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Minus, Maximize2, Minimize2, X, ArrowUpCircle } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { mediaKeys } from "@/queries/keys";
import { useAppStore } from "@/stores";
import { UploadPageContent } from "@/features/upload/upload-page";

export function GlobalCaptureDialog() {
  const captureDialog = useAppStore((s) => s.captureDialog);
  const minimized = useAppStore((s) => s.captureDialogMinimized);
  const maximized = useAppStore((s) => s.captureDialogMaximized);
  const close = useAppStore((s) => s.closeCaptureDialog);
  const setMinimized = useAppStore((s) => s.setCaptureDialogMinimized);
  const setMaximized = useAppStore((s) => s.setCaptureDialogMaximized);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!captureDialog || minimized) return;
    panelRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const appRoot = document.getElementById("root");
    appRoot?.setAttribute("inert", "");

    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const nested = (document.activeElement as HTMLElement | null)?.closest('[role="dialog"]');
      if (nested && nested !== panelRef.current) return;
      setMinimized(true);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      appRoot?.removeAttribute("inert");
    };
  }, [captureDialog, minimized, setMinimized]);

  if (!captureDialog || typeof document === "undefined") return null;

  const mode = captureDialog.mode;
  const title =
    mode === "translate"
      ? t("TRANSLATE_FILE.TITLE", "Translate File")
      : t("UPLOAD.DIALOG_TITLE", "Transcription Upload");

  const handleNavigate = (
    target: "folder" | "file" | "dashboard",
    id?: string,
    folderId?: string,
  ) => {
    queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
    close();
    if (target === "file" && id && folderId)
      navigate(`/folder/${encodeURIComponent(folderId)}/insight/${encodeURIComponent(id)}`);
    else if (target === "file" && id) navigate("/home");
    else if (target === "folder" && folderId) navigate(`/folder/${encodeURIComponent(folderId)}`);
    else if (target === "folder") navigate("/home");
  };

  const iconBtnClass =
    "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

  return createPortal(
    <>
      {!minimized && (
        <div
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={() => setMinimized(true)}
          aria-hidden="true"
        />
      )}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal={!minimized}
        aria-label={title}
        className={cn(
          "fixed z-[61] flex flex-col overflow-hidden border border-border bg-card shadow-xl outline-none transition-all",
          minimized
            ? "bottom-4 right-4 w-72 rounded-lg"
            : maximized
              ? "inset-0"
              : "inset-x-3 top-[3vh] bottom-[3vh] mx-auto max-w-6xl rounded-xl sm:inset-x-6",
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {minimized && <ArrowUpCircle aria-hidden="true" className="h-4 w-4 text-primary shrink-0" />}
            <h2 className="text-sm font-semibold truncate">{title}</h2>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {minimized ? (
              <button
                type="button"
                onClick={() => setMinimized(false)}
                aria-label={t("ACTION.RESTORE", "Restore")}
                title={t("ACTION.RESTORE", "Restore")}
                className={iconBtnClass}
              >
                <Maximize2 aria-hidden="true" className="h-4 w-4" />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setMinimized(true)}
                  aria-label={t("ACTION.MINIMIZE", "Minimize")}
                  title={t("ACTION.MINIMIZE", "Minimize")}
                  className={iconBtnClass}
                >
                  <Minus aria-hidden="true" className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setMaximized(!maximized)}
                  aria-label={maximized ? t("ACTION.RESTORE", "Restore") : t("ACTION.MAXIMIZE", "Maximize")}
                  title={maximized ? t("ACTION.RESTORE", "Restore") : t("ACTION.MAXIMIZE", "Maximize")}
                  className={iconBtnClass}
                >
                  {maximized ? (
                    <Minimize2 aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <Maximize2 aria-hidden="true" className="h-4 w-4" />
                  )}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={close}
              aria-label={t("ACTION.CLOSE", "Close")}
              title={t("ACTION.CLOSE", "Close")}
              className={iconBtnClass}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>

        {minimized && (
          <button
            type="button"
            onClick={() => setMinimized(false)}
            className="px-4 py-2.5 text-left text-xs text-muted-foreground hover:bg-muted transition-colors"
          >
            {t("UPLOAD.MINIMIZED_HINT", "Running in the background. Click to expand")}
          </button>
        )}

        <div className={cn("flex-1 min-h-0 overflow-y-auto px-6 py-5", minimized && "hidden")}>
          <Suspense fallback={null}>
            <UploadPageContent
              isDialog
              mode={mode}
              seedFolderId={captureDialog.folderId}
              onNavigate={handleNavigate}
            />
          </Suspense>
        </div>
      </div>
    </>,
    document.body,
  );
}
